/**
 * Os cadastros das Configurações que o Gate 2 ainda não tinha portado:
 * atendimento e CRM, categorias de procedimento, pacotes do catálogo,
 * preferências e a assinatura vista pela própria clínica.
 *
 * Em arquivo próprio, e não em `tipos.ts`, porque são um bloco que anda junto —
 * a migração `0080` cria as tabelas de todos eles de uma vez.
 */

import type { CicloDoPlano, Fatura, InadimplenciaDaClinica, SituacaoDaClinica } from './tipos';

/**
 * Paleta de filas e etiquetas.
 *
 * Sem tons de vinho de propósito: bordô é o acento da marca, e etiqueta em
 * bordô se confunde com estado selecionado. É a paleta do protótipo.
 */
export const CORES_OPERACIONAIS = ['#2986FF', '#22C55E', '#B87503', '#8B5CF6', '#0EA5A4', '#EC7C1B', '#7E8192', '#E5484D'] as const;

/* ------------------------------------------------------ atendimento e CRM */

export interface Departamento {
    id: string;
    nome: string;
    descricao: string;
    ativo: boolean;
    /** Filas penduradas nele. */
    filas: number;
    /** Pessoas vinculadas a alguma fila dele, sem repetir. */
    usuarios: number;
}

export interface DadosDoDepartamento {
    nome: string;
    descricao: string;
    ativo: boolean;
}

/**
 * Como a fila entrega conversa sozinha (0161), só para quem está online.
 * `manual` não entrega: a conversa espera em Pendentes, livre para a fila.
 */
export type ModoDeDistribuicao = 'manual' | 'aleatorio' | 'balanceado' | 'sequencial';

export const MODOS_DE_DISTRIBUICAO: { id: ModoDeDistribuicao; rotulo: string; descricao: string }[] = [
    { id: 'manual', rotulo: 'Manual', descricao: 'Ninguém recebe sozinho: quem é da fila aceita.' },
    { id: 'aleatorio', rotulo: 'Aleatório', descricao: 'Sorteia entre quem está online.' },
    { id: 'balanceado', rotulo: 'Balanceado', descricao: 'Vai para quem tem menos conversas abertas e reservadas.' },
    { id: 'sequencial', rotulo: 'Sequencial', descricao: 'Um de cada vez, em rodízio, na ordem do nome.' },
];

export function rotuloDaDistribuicao(modo: ModoDeDistribuicao): string {
    return MODOS_DE_DISTRIBUICAO.find((m) => m.id === modo)?.rotulo ?? modo;
}

export interface FilaDeAtendimento {
    id: string;
    nome: string;
    departamentoId: string;
    cor: string;
    /** Tempo de resposta esperado, em minutos. */
    slaMin: number;
    ativa: boolean;
    distribuicao: ModoDeDistribuicao;
    membros: { id: string; nome: string }[];
}

export interface DadosDaFila {
    nome: string;
    departamentoId: string;
    cor: string;
    slaMin: number;
    ativa: boolean;
    distribuicao: ModoDeDistribuicao;
    membrosIds: string[];
}

export interface Etiqueta {
    id: string;
    nome: string;
    cor: string;
    departamentoId?: string;
    filaId?: string;
    /** Vazio é etiqueta manual. */
    gatilhos: string[];
    ativa: boolean;
}

export interface DadosDaEtiqueta {
    nome: string;
    cor: string;
    departamentoId?: string;
    filaId?: string;
    gatilhos: string[];
    ativa: boolean;
}

/** Pública: toda a equipe. Privada: só quem escreveu. Gestão: só quem administra. */
export type EscopoDaMensagemRapida = 'publica' | 'privada' | 'gestao';

export interface MensagemRapida {
    id: string;
    gatilho: string;
    titulo: string;
    texto: string;
    escopo: EscopoDaMensagemRapida;
    usos: number;
    ativa: boolean;
}

export interface DadosDaMensagemRapida {
    gatilho: string;
    titulo: string;
    texto: string;
    escopo: EscopoDaMensagemRapida;
    ativa: boolean;
}

export interface CargaDeAtendimentoECrm {
    departamentos: Departamento[];
    filas: FilaDeAtendimento[];
    etiquetas: Etiqueta[];
    mensagensRapidas: MensagemRapida[];
    /** Quem pode ser vinculado a uma fila: a equipe ativa. */
    membros: { id: string; nome: string }[];
    /** Quem está logado pode criar mensagem restrita à gestão? */
    podeGestao: boolean;
}

/** As opções de tempo de resposta do protótipo, em minutos. */
export const SLAS_DA_FILA: { minutos: number; rotulo: string }[] = [
    { minutos: 2, rotulo: '2 min' },
    { minutos: 5, rotulo: '5 min' },
    { minutos: 15, rotulo: '15 min' },
    { minutos: 30, rotulo: '30 min' },
    { minutos: 60, rotulo: '1 h' },
    { minutos: 240, rotulo: '4 h' },
];

export function rotuloDoSla(minutos: number): string {
    const conhecido = SLAS_DA_FILA.find((s) => s.minutos === minutos);
    if (conhecido) return conhecido.rotulo;
    return minutos % 60 === 0 ? `${minutos / 60} h` : `${minutos} min`;
}

/** O atalho vira `/gatilho`: minúsculo, sem acento e sem espaço. */
export function normalizarGatilho(bruto: string): string {
    return bruto
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toLowerCase().trim()
        .replace(/^\/+/, '')
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
}

/* ------------------------------------------------ procedimentos e pacotes */

export interface CategoriaDeProcedimento {
    id: string;
    nome: string;
    ativa: boolean;
    /** Procedimentos com esta categoria hoje. */
    procedimentos: number;
}

export interface PacoteDoCatalogo {
    id: string;
    nome: string;
    procedimentoId?: string;
    procedimento?: string;
    /** Ausente quando o pacote é por período, e não por sessões. */
    sessoes?: number;
    precoCentavos: number;
    validadeDias?: number;
    ativo: boolean;
}

export interface DadosDoPacoteDoCatalogo {
    nome: string;
    procedimentoId?: string;
    sessoes?: number;
    precoCentavos: number;
    validadeDias?: number;
    ativo: boolean;
}

/* ------------------------------------------------------------ preferências */

/** O valor de uma preferência composta é um pequeno objeto; o das simples, escalar. */
export type ValorComposto = { [campo: string]: string | number | boolean | string[] };
export type ValorDePreferencia = string | number | boolean | ValorComposto;

export interface PreferenciaSalva {
    chave: string;
    valor: ValorDePreferencia;
}

/* ------------------------------------------------------------- assinatura */

/** A assinatura como a PRÓPRIA clínica a enxerga: leitura, sem valores de outras. */
export interface AssinaturaVistaPelaClinica {
    plano: string;
    ciclo: CicloDoPlano;
    /** Ausente enquanto a VecchIA não decidiu o preço — não é zero. */
    precoCentavos?: number;
    situacao: SituacaoDaClinica;
    inicio?: string;
    proximaCobranca?: string;
    canceladaEm?: string;
    /** Último dia do teste de quem se cadastrou sozinho com cartão (0150): é o dia da 1ª cobrança. */
    testeAte?: string;
    /** Como o gateway cobra a assinatura do autocadastro. Ausente na cobrança por fatura manual. */
    formaDePagamento?: 'cartao' | 'pix_automatico';
    /** Os 4 últimos dígitos do cartão da assinatura. */
    cartaoFinal?: string;
    limiteMembros?: number;
    limiteUnidades?: number;
    faturas: Fatura[];
    /** Fatura vencida em aberto (trilha T9): o aviso, e o bloqueio se passou de 15 dias. */
    inadimplencia?: InadimplenciaDaClinica;
}
